/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 *      keep in sync with the code):
 *   role:      lib — splits the tenant free CSS of the shop (open_advanced
 *              option1) into its top-level `@import` statements and the rest,
 *              so the imports can be printed in their own <style> BEFORE any
 *              other tenant rule.
 *   exports:   splitTenantCss(css), layerImport(statement, layer)
 *   callers:   helpers/tenant-css-split.js (templates/application.hbs),
 *              components/ec-tenant-css-imports.js
 *   tests:     tenant-css-split-test.js, ui-nuova-ec-shell-test.js
 *   gotchas:   an `@import` is honoured only when it precedes every other rule
 *              (only `@charset` / `@layer` statements may come first) and never
 *              inside a block. option1 used to be printed after the loader
 *              colour rule and, since M18, inside `@layer classica { … }`
 *              (BbClassicStyle): every tenant `@import` was silently dropped by
 *              the browser (sansicarioaction: Orbitron, a font-awesome sheet,
 *              simple-line-icons never loaded). `@charset` is meaningless in a
 *              <style> element (the document is already decoded): dropped.
 *              When nothing is extracted `rest` is the input itself, so the
 *              classic output of a tenant without `@import` is byte-identical.
 */

const LAYER_KEYWORD = /^layer\b/i;

/**
 * Index just past the string literal opened at `start` (quote included), or
 * the input length for an unterminated string.
 */
function skipString(css, start) {
  const quote = css[start];
  let i = start + 1;
  while (i < css.length) {
    const ch = css[i];
    if (ch === '\\') {
      i += 2;
      continue;
    }
    if (ch === quote || ch === '\n') return i + 1;
    i++;
  }
  return css.length;
}

/** Index just past the comment opened at `start`, or the input length. */
function skipComment(css, start) {
  const end = css.indexOf('*/', start + 2);
  return end === -1 ? css.length : end + 2;
}

/**
 * Index just past the `;` that ends the at-rule prelude starting at `start`
 * (strings, comments and parentheses skipped), or the input length when the
 * statement is unterminated.
 */
function statementEnd(css, start) {
  let depth = 0;
  let i = start;
  while (i < css.length) {
    const ch = css[i];
    if (ch === '"' || ch === "'") {
      i = skipString(css, i);
      continue;
    }
    if (ch === '/' && css[i + 1] === '*') {
      i = skipComment(css, i);
      continue;
    }
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === ';' && depth === 0) return i + 1;
    else if ((ch === '{' || ch === '}') && depth === 0) return i;
    i++;
  }
  return css.length;
}

/** `@import` / `@charset` keyword (case-insensitive) at index `i`. */
function atKeyword(css, i) {
  const match = /^@(import|charset)(?![\w-])/i.exec(css.slice(i, i + 9));
  return match ? match[1].toLowerCase() : null;
}

/**
 * Split the tenant CSS into `{ imports, rest }`:
 * - imports: every top-level `@import …;` statement, trimmed, `;` guaranteed;
 * - rest:    the input without those statements and without `@charset`.
 * Comments and strings are respected (an `@import` inside a comment, a string
 * or a block stays in `rest`).
 *
 * @param {unknown} css
 * @returns {{ imports: string[], rest: unknown }}
 */
export function splitTenantCss(css) {
  if (typeof css !== 'string' || !css.includes('@')) return { imports: [], rest: css };

  const imports = [];
  let rest = '';
  let copyFrom = 0;
  let depth = 0;
  let i = 0;
  while (i < css.length) {
    const ch = css[i];
    if (ch === '"' || ch === "'") {
      i = skipString(css, i);
      continue;
    }
    if (ch === '/' && css[i + 1] === '*') {
      i = skipComment(css, i);
      continue;
    }
    if (ch === '{') depth++;
    else if (ch === '}') depth = Math.max(0, depth - 1);
    else if (ch === '@' && depth === 0) {
      const keyword = atKeyword(css, i);
      if (keyword) {
        const end = statementEnd(css, i);
        if (keyword === 'import') {
          const statement = css.slice(i, end).trim();
          imports.push(statement.endsWith(';') ? statement : `${statement};`);
        }
        rest += css.slice(copyFrom, i);
        copyFrom = end;
        i = end;
        continue;
      }
    }
    i++;
  }
  if (copyFrom === 0) return { imports, rest: css };
  rest += css.slice(copyFrom);
  return { imports, rest };
}

/**
 * Put one `@import …;` statement into the cascade layer `layer`: the
 * `layer(name)` clause goes right after the URL (before any supports()/media
 * condition, as the grammar requires). A statement that already names a layer,
 * or whose URL cannot be read, is returned unchanged.
 *
 * @param {string} statement a full `@import …;` statement
 * @param {string} layer
 * @returns {string}
 */
export function layerImport(statement, layer) {
  const head = /^@import\s*/i.exec(statement);
  if (!head) return statement;
  let i = head[0].length;
  const ch = statement[i];
  if (ch === '"' || ch === "'") {
    i = skipString(statement, i);
  } else if (/^url\(/i.test(statement.slice(i))) {
    i += 4;
    while (i < statement.length && statement[i] !== ')') {
      i = statement[i] === '"' || statement[i] === "'" ? skipString(statement, i) : i + 1;
    }
    if (i >= statement.length) return statement;
    i++;
  } else {
    return statement;
  }
  const tail = statement.slice(i);
  if (LAYER_KEYWORD.test(tail.trimStart())) return statement;
  return `${statement.slice(0, i)} layer(${layer})${tail}`;
}
